"use client"

import { generateImagePath } from "@/lib/utils"
import { ComponentConfig } from "@measured/puck"
import Image from "next/image"
import { useEffect, useRef, useState } from "react"
import { twMerge } from "tailwind-merge"
import { CommonStylesProps } from "../../../lib/commonCSSProps"
import { generateResponsiveCSS } from "../../../lib/helper"

export interface WhyChooseUsProps extends CommonStylesProps {
  features?: {
    icon?: string
    activeIcon?: string
    title?: string
    description?: string
    image?: string | { url: string; alternativeText?: string }
  }[]
}

export const RenderConfig: ComponentConfig<WhyChooseUsProps> = {
  render: ({ className, styles, responsiveType, features = [] }) => {
    const id = `why-choose-us-${Math.random().toString(36).substr(2, 9)}`
    const responsiveCSS = generateResponsiveCSS(id, styles || {}, responsiveType)
    const [activeIndex, setActiveIndex] = useState(0)
    const leftColRef = useRef<HTMLDivElement | null>(null)
    const [rightHeight, setRightHeight] = useState<number | undefined>(undefined)

    useEffect(() => {
      const updateHeight = () => {
        const h = leftColRef.current?.offsetHeight
        if (h && h > 0) setRightHeight(h)
      }
      updateHeight()
      window.addEventListener("resize", updateHeight)
      return () => window.removeEventListener("resize", updateHeight)
    }, [features, activeIndex])

    return (
      <>
        {responsiveCSS}
        <div id={id} className={twMerge(className, "w-full")}>
          <div className="flex sm:flex-row flex-col justify-center gap-[80px] items-stretch">
            <div ref={leftColRef} className="space-y-6 sm:w-[49.2%] justify-center flex flex-col">
              {features.map((feature, index) => (
                <div
                  key={index}
                  onClick={() => setActiveIndex(index)}
                  className={`p-6 rounded-xl cursor-pointer transition-all duration-300 border border-[#F4F0FF] ${
                    activeIndex === index ? "bg-[#0C9443] text-white" : "bg-white hover:shadow-lg"
                  }`}
                >
                  <div className="flex items-center gap-6">
                    <div
                      className={`rounded-xl flex items-center justify-center p-2.5 ${
                        activeIndex === index ? "bg-white" : "bg-[#0C9443]"
                      }`}
                    >
                      <div className="w-6 h-6">
                        <Image
                          src={generateImagePath(activeIndex === index ? feature.activeIcon : feature.icon, "original")}
                          alt={`${feature.title} Icon`}
                          width={24}
                          height={24}
                          className="w-full h-full object-contain"
                        />
                      </div>
                    </div>

                    <div className="flex flex-col gap-1">
                      {feature.title && (
                        <h3
                          className={`text-lg leading-6 font-semibold ${
                            activeIndex === index ? "text-white" : "text-[#0E0101]"
                          }`}
                        >
                          {feature.title}
                        </h3>
                      )}
                      {feature.description && (
                        <p
                          className={`text-base leading-[27px] font-normal ${
                            activeIndex === index ? "text-white" : "text-[#4F4F4F]"
                          }`}
                        >
                          {feature.description}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="relative w-[50.8%] sm:flex hidden flex-col" style={{ height: rightHeight }}>
              {features.length > 0 && (
                <>
                  <div className="relative rounded-2xl overflow-hidden flex-1 h-full">
                    <Image
                      src={generateImagePath(features[activeIndex]?.image, "medium")}
                      alt={
                        features[activeIndex]?.image
                          ? typeof features[activeIndex]?.image === "string"
                            ? features[activeIndex]?.title || "Feature Image"
                            : features[activeIndex]?.image.alternativeText ||
                              features[activeIndex]?.title ||
                              "Feature Image"
                          : features[activeIndex]?.title || "Feature Image"
                      }
                      width={600}
                      height={400}
                      className="w-full h-full object-cover hover:cursor-pointer hover:scale-105 transition-all duration-300"
                    />
                    {features.length > 1 && (
                      <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex justify-center space-x-2">
                        {features.map((_, index) => (
                          <button
                            key={index}
                            onClick={() => setActiveIndex(index)}
                            className="p-1 bg-[#FFFFFF80] rounded-full transition-all duration-300"
                          >
                            <div
                              className={`h-[6px] rounded-full transition-all duration-300 ${
                                activeIndex === index ? "bg-[#00A19A] w-3 h-[6px]" : "bg-white w-[6px] hover:bg-white"
                              }`}
                            />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </>
    )
  },
}
